// Default value of Orion settings.
export const defaultSettings = {
  enable_fuzzy_public_search: false, // When true, the public (clip) search will use fuzzy matching.
  hide_storage_banner_video_page: false,
  onboarding_survey_skip_to_paywall: false,
  onboarding_paywall_kind: 'legacy' as 'legacy' | 'plansComparison' | 'featureList',
  onboarding_paywall_tier: 'starter' as 'starter' | 'standard' | 'advanced',
  onboarding_paywall_default_periodicity: 'yearly' as 'yearly' | 'monthly',
  onboarding_paywall_monthly_free_trial: false,
  onboarding_paywall_dismiss_button_position: 'top_right' as 'top_right' | 'bottom',
  late_stage_paywall_kind: 'legacy' as
    | 'legacy'
    | 'limit_based_bsp'
    | 'limits_only_bsp'
    | 'bsp_cards_generic_and_bsp_limits'
    | 'bsp_table_generic_and_bsp_limits',
  limit_based_paywall_tier: 'one_up' as
    | 'one_up'
    | 'standard'
    | 'advanced'
    | 'creator'
    | 'professional',
  cold_storage_trigger_paywall_tier: 'one_up' as
    | 'one_up'
    | 'standard'
    | 'advanced'
    | 'creator'
    | 'professional',
  campaign_id_override: 'null' as 'null' | 'pi_2026' | CampaignIdOverride,
  review_accountless_comment_redesign: false,
  checkout_experience: 'legacy' as
    | 'legacy'
    | 'disclosureVisiblePrice'
    | 'disclosureVisiblePriceCompliantV1'
    | 'disclosureVisiblePriceCompliantV2',
  checkout_default_payment_method: 'credit_card' as 'credit_card' | 'paypal',
  orion_free_override: false, // When true, users get REPACK_FREE product instead of default free tier.
  use_juno_billing: false,
  enable_new_library_drag_and_drop_upload: false,
  add_to_folders_modal_select_all_folders: false,
  showcase_autoplay_video_preview: false,
  enable_no_expiration_review_link_option: false,
  bi_managed_client_accounts_fake_door_enabled: false,
  bi_white_label_clip_pages_fake_door_enabled: false,
  bi_expiring_links_ux_enabled: false,
  bsp_cancellation_flow: false,
  cancellation_offer_enabled: false,
  cancellation_survey_kind: 'select_option' as 'none' | 'select_option' | 'fulltext_question',
  lapsing_clip_selection_cancellation_ux: false,
  use_resource_name_for_review_links: false,
  enable_review_link_default_settings: false,
  enable_review_link_end_cards: false,
  show_profile_reorder_modal: false,
  show_downgrade_card_billing: false,
  enable_rename_folder: false,
  enable_rename_video: false,
  enable_copy_button_video: false,
  enable_rename_showcase_and_events: false,
  enable_privacy_badge_modal: false,
  enable_review_page_shortcut_from_main: false,
  enable_profile_hover_video_preview: false,
  is_new_live_events_dashboard_enabled: false,
  live_google_slides_integration: false,
  creator_monetisation_creator_enabled: false,
  creator_monetisation_viewer_treatment: 'false' as 'false' | 'donation' | 'subscription',
  mweb_to_app_redirect: 'control' as 'control' | 'smart_banner' | 'custom_modal',
  enable_viewer_home_suggestions_api: false,
  enable_billing_menu_item: false,
  auto_select_uploaded_thumbnail: false,
  free_trial_banner_tier: 'none' as 'none' | 'low_tier' | 'mid_tier' | 'high_tier',
  show_trial_renewal_date_in_billing_card: false,
  enable_review_link_submenu: false,
  should_switch_to_repackaging_free: false,
  should_switch_to_higher_storage_free: false,
  should_have_permissive_review_links: false,
  enable_seat_changes_for_tiered: false,
  upload_max_simultaneous_uploads: 1,
  enable_omnisearch_folder_scoped_request_fix: false,
  enable_showcase_search: false,
  ask_your_library_enabled: false, // When true, expose the Ask Your Library AI search experience.
  new_moments_enabled: false, // When true, expose the new Moments search experience.
  enable_uploader_tooltip: false,
  viewer_ai_upsell_modal_enabled: false,
  background_embed_upsell_enabled: false,
  trustpilot_review_modal: false,
  profile_redesign: false,
  enable_original_download_banner: false,
  enable_avatar_initials_fallback: false,
  enable_list_view_folder_upload: false,
  enable_cold_storage_library_ux: false,
  // Gates the cold-privacy UI (e.g. the cold-storage banner's privacy-restriction
  // states). Driven by the backend `is_cold_privacy_restricted` signal; built on
  // top of the backend-driven cold-storage UX (NOT the legacy
  // `enable_cold_storage_library_ux` frontend experiment).
  cold_privacy_enabled: false,
  remove_watermark_free_tier: false,
  maximize_video_file_transfer_adoption: false,
  maximize_review_adoption: false,
  enable_single_video_rendition_status: false,
  hp_recents_number_of_videos: 10,
  hp_remove_search_params_results: false,
  enable_audio_download: false,
  enable_svv_metadata_save_button: false,
  viewer_segmentation_redirect_to_watch: false,
  enable_home_grace_period_notifications: false,
  enable_video_page_grace_period_notifications: false,
  enable_watch_page_grace_period_notifications: false,
  show_custom_metadata: false,
  privacy_options_centralized: false,
  privacy_settings_new_copy: false,
  add_new_thumbnail_modal: false,
  video_menu_thumbnail_modal: false,
  clip_page_player_new_speed_menu_enabled: false,
  viewer_premium_plan_v0: 'control' as
    | 'control'
    | 'hard_dismiss_monthly'
    | 'easy_dismiss_monthly'
    | 'easy_dismiss_yearly',
  // Legacy arm letters (A/B/C) and the equivalent semantic values are both
  // accepted so the experiment platform can migrate at its own pace:
  // A === 'accordion', B === 'flat', C === 'hidden' (control).
  bulk_privacy_modal_variant: 'hidden' as 'A' | 'B' | 'C' | 'accordion' | 'flat' | 'hidden',
  business_plans_enforced: false,
  in_product_support_panel: false,
  reverse_free_trial_arm: 'control' as 'control' | '7d' | '14d',
  corporate_upgrade_modal_page: 'billing' as 'billing' | 'home',
  has_recently_deleted: false,
  video_page_customization_v1: false,
  enable_event_series: false,
  analytics_freshness_refactor_main: false, // When true uses refactored analytics freshness.
  analytics_freshness_refactor_watch_history: false, // When true uses refactored analytics freshness.
} as const;

export type CampaignIdOverride =
  | 'pi_2026_storage_seat'
  | 'pi_2026_legacy_plans'
  | 'rp_2026'
  | 'rp_2026_strict';

/// Type of the Orion settings.
export type OrionSettings = typeof defaultSettings;
